import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {Observable, of, Subscription, switchMap} from 'rxjs';
import {TenantLoginModel} from 'src/app/models/tenants/tenant-login.model';
import {AuthorizationService} from 'src/app/services/authorization.service';
import {LoadingService} from 'src/app/services/loading.service';
import {LoginService} from 'src/app/services/login.service';
import {distinctUntilChanged, tap} from "rxjs/operators";
import { AppRoutes } from 'src/app/constants/app-routes';
import { environment } from 'src/environments/environment';

@Component({
    selector: 'app-login',
    standalone: false,
    templateUrl: './login.component.html',
    styleUrl: './login.component.scss',
})
export class LoginComponent implements OnInit, OnDestroy {
    @Input() previewMode = false;
    @Input() previewBackgroundUrl?: string | null = null;
    @Input() previewLogoUrl?: string | null = null;

    company: string | null = null;
    loginConfig: TenantLoginModel | null = null;
    subs = new Subscription();

    private returnUrl: string = AppRoutes.dashboard;

    readonly defaultBackgroundImageUrl = 'https://file.skeddle.com/skeddle/_b2c/templates/business-dev/background.jpg';
    readonly defaultLogoImageUrl = 'https://file.skeddle.com/skeddle/_b2c/templates/business-dev/logo.jpg';
    readonly loginInitiated$: Observable<boolean>;

    constructor(
        private loginService: LoginService,
        private loadingService: LoadingService,
        private router: Router,
        private route: ActivatedRoute,
        private authorizationService: AuthorizationService
    ) {
        this.loginInitiated$ = authorizationService.loginInitiated$.pipe(
            tap(loginInitiated => {console.log('initiated', loginInitiated)}),
        );
    }

    ngOnInit(): void {
        if (this.previewMode) {
            this.loginConfig = {
                backgroundUrl: this.previewBackgroundUrl ?? null,
                logoUrl: this.previewLogoUrl ?? null,
                singleSignOn: false
            };
            return;
        }
        this.company = this.getCompanyName();

        this.returnUrl = this.route.snapshot.queryParams['returnUrl'] || AppRoutes.dashboard;
        const sub = this.authorizationService.isAuthenticated$.pipe(
            distinctUntilChanged(),
            switchMap(isAuthenticated => {
                console.log('isAuthenticated', this.authorizationService.isAuthenticatedSnapshot);
                if (isAuthenticated && this.router.url !== this.returnUrl) {
                    console.log('Route to return URL')
                    this.router.navigateByUrl(this.returnUrl);
                    return of(null);
                } else if (!isAuthenticated && this.company) {
                    return this.loadingService.load(
                        this.loginService.getTenantLoginConfig(this.company)
                    );
                } else {
                    return of(null)
                }
            })
        ).subscribe((config) => {
            this.loginConfig = config;
        });

        this.subs.add(sub);
    }

    ngOnDestroy(): void {
        this.subs.unsubscribe();
    }

    onLoginClick(): void {
        if (this.previewMode) return;
        this.authorizationService.login();
    }

    get companyLoginAvailable(): boolean {
        return !!(this.loginConfig?.singleSignOn && this.loginConfig.federationDomainHint?.trim());
    }

    onCompanyLoginClick(): void {
        if (this.previewMode) return;
        const config = this.loginConfig;
        if (!config?.singleSignOn || !config.federationDomainHint?.trim()) {
            throw new Error('Company login is not configured for this tenant.');
        }

        this.authorizationService.loginWithCompanyAccount(config.federationDomainHint);
    }

    getCompanyName(): string {
        const hostname = window.location.hostname;
        const rootDomain = environment.rootDomain;

        if (hostname === 'localhost' || hostname === '127.0.0.1') {
            return environment.defaultTenant;
        }

        if (!hostname.endsWith(rootDomain)) {
            return hostname;
        }

        return hostname.replace(`.${rootDomain}`, '')
    }
}
